// UI strings for the card meaning pages.
// Card content (meanings text) lives in src/data/cardMeanings/content.<lang>.json.
import zhUi from '../../data/cardMeanings/ui.zh.json';
import deUi from '../../data/cardMeanings/ui.de.json';
import plUi from '../../data/cardMeanings/ui.pl.json';
import ptUi from '../../data/cardMeanings/ui.pt.json';
import frUi from '../../data/cardMeanings/ui.fr.json';

const LANGUAGE_NAMES = {
  en: 'English',
  es: 'Español',
  ar: 'العربية',
  zh: '中文',
  de: 'Deutsch',
  pl: 'Polski',
  pt: 'Português',
  fr: 'Français',
};

const fillTemplate = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template,
  );

const compileUi = (ui, lang, localeTag) => ({
  ...ui,
  dir: 'ltr',
  siteName: 'NowTarot',
  askPath: `/${lang}/`,
  yesNoPath: '/yes-or-no-tarot/',
  drawPath: '/free-tarot/',
  localeTag,
  languageNames: LANGUAGE_NAMES,
  cardTitle: (name) => fillTemplate(ui.cardTitle, { name }),
  cardH1: (name) => fillTemplate(ui.cardH1, { name }),
  cardDescription: (name) => fillTemplate(ui.cardDescription, { name }),
  keywordsHeading: (name) => fillTemplate(ui.keywordsHeading, { name }),
  descriptionHeading: (name) => fillTemplate(ui.descriptionHeading, { name }),
  uprightHeading: (name) => fillTemplate(ui.uprightHeading, { name }),
  reversedHeading: (name) => fillTemplate(ui.reversedHeading, { name }),
  yesNoHeading: (name) => fillTemplate(ui.yesNoHeading, { name }),
  askCtaHeadingForCard: (name) => fillTemplate(ui.askCtaHeadingForCard, { name }),
  askCompactHeading: (name) => fillTemplate(ui.askCompactHeading, { name }),
  ownSpreadTitle: (name) => fillTemplate(ui.ownSpreadTitle, { name }),
  moreInArcana: (arcanaLabel) => fillTemplate(ui.moreInArcana, { arcanaLabel }),
  cardOfTheDayIntro: (dateLabel) => fillTemplate(ui.cardOfTheDayIntro, { dateLabel }),
  readFullMeaning: (name) => fillTemplate(ui.readFullMeaning, { name }),
});

const STRINGS = {
  en: {
    dir: 'ltr',
    siteName: 'NowTarot',
    hubTitle: 'Tarot Card Meanings: All 78 Cards, Upright & Reversed | NowTarot AI',
    hubDescription:
      'Complete guide to all 78 tarot card meanings. Upright and reversed interpretations for love, career, and money — Major Arcana, Wands, Cups, Swords, and Pentacles.',
    hubHeading: 'Tarot Card Meanings',
    hubIntro:
      'Explore the meaning of every card in the 78-card tarot deck. Each guide covers the upright and reversed meaning, plus what the card says about love, career, and money — and whether it answers yes or no to your question.',
    cardTitle: (name) => `${name} Tarot Card Meaning: Upright & Reversed | NowTarot AI`,
    cardH1: (name) => `${name} Tarot Card Meaning`,
    cardDescription: (name) =>
      `${name} tarot card meaning explained: upright and reversed, plus love, career, and money interpretations — and whether ${name} means yes or no in your reading.`,
    breadcrumbHome: 'Home',
    breadcrumbHub: 'Tarot Card Meanings',
    arcana: {
      major: 'Major Arcana',
      wands: 'Suit of Wands',
      cups: 'Suit of Cups',
      swords: 'Suit of Swords',
      pentacles: 'Suit of Pentacles',
    },
    keywordsHeading: (name) => `${name} Keywords`,
    uprightKeywords: 'Upright keywords',
    reversedKeywords: 'Reversed keywords',
    descriptionHeading: (name) => `${name} Card Symbolism`,
    uprightHeading: (name) => `${name} Upright Meaning`,
    reversedHeading: (name) => `${name} Reversed Meaning`,
    upright: 'Upright',
    reversed: 'Reversed',
    love: 'Love & Relationships',
    career: 'Career & Work',
    finances: 'Money & Finances',
    careerMoney: 'Career & Money',
    jumpLove: 'Love',
    jumpCareer: 'Career',
    jumpYesNo: 'Yes or No',
    jumpSymbolism: 'Symbolism',
    onThisPage: 'On this page',
    yesNoHeading: (name) => `${name}: Yes or No?`,
    polarity: {
      positive: 'Yes',
      negative: 'No',
      neutral: 'Maybe',
      'neutral-positive': 'Leaning Yes',
      'neutral-negative': 'Leaning No',
    },
    yesNoCta: 'Ask your own question with a free Yes or No tarot draw',
    yesNoPath: '/yes-or-no-tarot/',
    drawCta: 'Draw this card yourself in a free tarot reading',
    drawPath: '/free-tarot/',
    askCtaHeading: 'Ask your question. Get a full AI reading.',
    askCtaHeadingForCard: (name) => `Done reading about ${name}? Ask your own question.`,
    askCompactHeading: (name) => `What does ${name} mean for you?`,
    askCompactText: 'Ask your own question and get a personalized tarot reading.',
    pulledCard: 'Pulled this card in your own reading? Interpret your full spread with AI →',
    ownSpreadTitle: (name) => `Pulled ${name} in your own spread?`,
    ownSpreadText: 'Enter the cards you drew from your physical deck and let AI interpret how they work together. Ask follow-up questions about your reading.',
    ownSpreadNote: 'Free · No account needed',
    ownSpreadCta: 'Interpret My Cards',
    askCtaText:
      'Type what you actually want to know. The AI draws a full spread and interprets the cards together for your situation — free, in seconds.',
    askCtaButton: 'Ask the AI Tarot Reader',
    askPath: '/',
    languageSwitcherLabel: 'Read this in:',
    languageNames: LANGUAGE_NAMES,
    prevCard: 'Previous card',
    nextCard: 'Next card',
    moreInArcana: (arcanaLabel) => `More cards from the ${arcanaLabel}`,
    allCards: 'All Tarot Card Meanings',
    cardOfTheDayTitle: 'Tarot Card of the Day — Your Free Daily Tarot Reading | NowTarot AI',
    cardOfTheDayDescription:
      "Today's tarot card of the day with its full meaning for love, career, and money. A free daily tarot reading, updated every day.",
    cardOfTheDayHeading: 'Tarot Card of the Day',
    cardOfTheDayIntro: (dateLabel) =>
      `Pick a card for ${dateLabel}. That card is yours until you draw again.`,
    cardOfTheDayPickPrompt: 'Choose one of the cards below.',
    drawNewCardOfTheDay: 'Draw new card of the day',
    todaysCard: 'Your card of the day',
    readFullMeaning: (name) => `Read the full ${name} meaning`,
    localeTag: 'en-US',
  },

  es: {
    dir: 'ltr',
    siteName: 'NowTarot',
    hubTitle: 'Significados de las Cartas del Tarot: Las 78 Cartas | NowTarot AI',
    hubDescription:
      'Guía completa de los significados de las 78 cartas del tarot. Interpretaciones al derecho y al revés para el amor, el trabajo y el dinero — Arcanos Mayores, Bastos, Copas, Espadas y Oros.',
    hubHeading: 'Significados de las Cartas del Tarot',
    hubIntro:
      'Explora el significado de cada carta del tarot de 78 cartas. Cada guía cubre el significado al derecho y al revés, además de lo que la carta dice sobre el amor, el trabajo y el dinero — y si responde sí o no a tu pregunta.',
    cardTitle: (name) => `${name}: Significado en el Tarot, al Derecho y al Revés | NowTarot AI`,
    cardH1: (name) => `Significado de ${name} en el tarot`,
    cardDescription: (name) =>
      `Significado de ${name} en el tarot: al derecho y al revés, con interpretaciones para el amor, el trabajo y el dinero — y si ${name} significa sí o no en tu lectura.`,
    breadcrumbHome: 'Inicio',
    breadcrumbHub: 'Significados de las Cartas',
    arcana: {
      major: 'Arcanos Mayores',
      wands: 'Palo de Bastos',
      cups: 'Palo de Copas',
      swords: 'Palo de Espadas',
      pentacles: 'Palo de Oros',
    },
    keywordsHeading: (name) => `Palabras Clave de ${name}`,
    uprightKeywords: 'Palabras clave al derecho',
    reversedKeywords: 'Palabras clave al revés',
    descriptionHeading: (name) => `Simbolismo de la carta ${name}`,
    uprightHeading: (name) => `Significado de ${name} al Derecho`,
    reversedHeading: (name) => `Significado de ${name} al Revés`,
    upright: 'Al derecho',
    reversed: 'Al revés',
    love: 'Amor y Relaciones',
    career: 'Trabajo y Carrera',
    finances: 'Dinero y Finanzas',
    careerMoney: 'Trabajo y dinero',
    jumpLove: 'Amor',
    jumpCareer: 'Trabajo',
    jumpYesNo: 'Sí o no',
    jumpSymbolism: 'Simbolismo',
    onThisPage: 'En esta página',
    yesNoHeading: (name) => `${name}: ¿Sí o No?`,
    polarity: {
      positive: 'Sí',
      negative: 'No',
      neutral: 'Quizás',
      'neutral-positive': 'Tiende a Sí',
      'neutral-negative': 'Tiende a No',
    },
    yesNoCta: 'Haz tu propia pregunta con una tirada gratis de Sí o No',
    yesNoPath: '/yes-or-no-tarot/',
    drawCta: 'Saca esta carta tú mismo en una lectura de tarot gratis',
    drawPath: '/free-tarot/',
    askCtaHeading: 'Haz tu pregunta. Recibe una lectura completa con IA.',
    askCtaHeadingForCard: (name) => `¿Terminaste de leer sobre ${name}? Haz tu propia pregunta.`,
    askCompactHeading: (name) => `¿Qué significa ${name} para ti?`,
    askCompactText: 'Haz tu propia pregunta y recibe una lectura de tarot personalizada.',
    pulledCard: '¿Sacaste esta carta en tu propia tirada? Interpreta toda tu tirada con IA →',
    ownSpreadTitle: (name) => `¿Sacaste ${name} en tu propia tirada?`,
    ownSpreadText: 'Introduce las cartas que sacaste de tu baraja física y deja que la IA interprete cómo funcionan juntas. Haz preguntas de seguimiento sobre tu lectura.',
    ownSpreadNote: 'Gratis · Sin cuenta',
    ownSpreadCta: 'Interpretar mis cartas',
    askCtaText:
      'Escribe lo que de verdad quieres saber. La IA saca una tirada completa e interpreta las cartas juntas para tu situación — gratis, en segundos.',
    askCtaButton: 'Preguntar al Tarot con IA',
    askPath: '/es/',
    languageSwitcherLabel: 'Leer esto en:',
    languageNames: LANGUAGE_NAMES,
    prevCard: 'Carta anterior',
    nextCard: 'Carta siguiente',
    moreInArcana: (arcanaLabel) => `Más cartas de ${arcanaLabel}`,
    allCards: 'Todos los Significados de las Cartas',
    cardOfTheDayTitle: 'Carta del Día — Tu Tarot Diario Gratis | NowTarot AI',
    cardOfTheDayDescription:
      'La carta del tarot de hoy con su significado completo para el amor, el trabajo y el dinero. Una lectura diaria de tarot gratis, actualizada cada día.',
    cardOfTheDayHeading: 'Carta del Tarot del Día',
    cardOfTheDayIntro: (dateLabel) =>
      `Elige una carta para el ${dateLabel}. Esa carta es tuya hasta que vuelvas a sacar.`,
    cardOfTheDayPickPrompt: 'Elige una de las cartas de abajo.',
    drawNewCardOfTheDay: 'Sacar una nueva carta del día',
    todaysCard: 'Tu carta del día',
    readFullMeaning: (name) => `Lee el significado completo de ${name}`,
    localeTag: 'es-ES',
  },

  ar: {
    dir: 'rtl',
    siteName: 'NowTarot',
    hubTitle: 'معاني بطاقات التاروت: كل البطاقات الـ78 | NowTarot AI',
    hubDescription:
      'دليل شامل لمعاني بطاقات التاروت الـ78. تفسيرات البطاقة المعتدلة والمقلوبة في الحب والعمل والمال — الأركانا الكبرى والعصي والكؤوس والسيوف والدنانير.',
    hubHeading: 'معاني بطاقات التاروت',
    hubIntro:
      'اكتشف معنى كل بطاقة من بطاقات التاروت الـ78. يشرح كل دليل معنى البطاقة المعتدلة والمقلوبة، وما تقوله عن الحب والعمل والمال — وهل تجيب بنعم أم لا على سؤالك.',
    cardTitle: (name) => `معنى بطاقة ${name} في التاروت: معتدلة ومقلوبة | NowTarot AI`,
    cardH1: (name) => `معنى بطاقة ${name} في التاروت`,
    cardDescription: (name) =>
      `معنى بطاقة ${name} في التاروت: التفسير المعتدل والمقلوب، مع تفسيرات للحب والعمل والمال — وهل تعني بطاقة ${name} نعم أم لا في قراءتك.`,
    breadcrumbHome: 'الرئيسية',
    breadcrumbHub: 'معاني بطاقات التاروت',
    arcana: {
      major: 'الأركانا الكبرى',
      wands: 'مجموعة العصي',
      cups: 'مجموعة الكؤوس',
      swords: 'مجموعة السيوف',
      pentacles: 'مجموعة الدنانير',
    },
    keywordsHeading: (name) => `الكلمات المفتاحية لبطاقة ${name}`,
    uprightKeywords: 'كلمات مفتاحية (معتدلة)',
    reversedKeywords: 'كلمات مفتاحية (مقلوبة)',
    descriptionHeading: (name) => `رمزية بطاقة ${name}`,
    uprightHeading: (name) => `معنى بطاقة ${name} المعتدلة`,
    reversedHeading: (name) => `معنى بطاقة ${name} المقلوبة`,
    upright: 'معتدلة',
    reversed: 'مقلوبة',
    love: 'الحب والعلاقات',
    career: 'العمل والمهنة',
    finances: 'المال والأمور المالية',
    careerMoney: 'العمل والمال',
    jumpLove: 'الحب',
    jumpCareer: 'العمل',
    jumpYesNo: 'نعم أو لا',
    jumpSymbolism: 'الرمزية',
    onThisPage: 'في هذه الصفحة',
    yesNoHeading: (name) => `بطاقة ${name}: نعم أم لا؟`,
    polarity: {
      positive: 'نعم',
      negative: 'لا',
      neutral: 'ربما',
      'neutral-positive': 'أقرب إلى نعم',
      'neutral-negative': 'أقرب إلى لا',
    },
    yesNoCta: 'اطرح سؤالك الخاص مع سحب مجاني لبطاقة نعم أو لا',
    yesNoPath: '/yes-or-no-tarot/',
    drawCta: 'اسحب هذه البطاقة بنفسك في قراءة تاروت مجانية',
    drawPath: '/free-tarot/',
    askCtaHeading: 'اطرح سؤالك واحصل على قراءة تاروت كاملة.',
    askCtaHeadingForCard: (name) => `انتهيت من القراءة عن ${name}؟ اطرح سؤالك.`,
    askCompactHeading: (name) => `ماذا تعني بطاقة ${name} لك؟`,
    askCompactText: 'اطرح سؤالك واحصل على قراءة تاروت خاصة بك.',
    pulledCard: 'هل سحبت هذه البطاقة في قراءتك الخاصة؟ فسّر انتشارك الكامل بالذكاء الاصطناعي →',
    ownSpreadTitle: (name) => `هل سحبت ${name} في انتشارك الخاص؟`,
    ownSpreadText: 'أدخل البطاقات التي سحبتها من مجموعتك الحقيقية، ودع الذكاء الاصطناعي يفسّر كيف تعمل معاً. ثم اسأل أسئلة متابعة عن قراءتك.',
    ownSpreadNote: 'مجاني · بلا حساب',
    ownSpreadCta: 'فسّر بطاقاتي',
    askCtaText:
      'اكتب ما تريد معرفته فعلاً. يسحب الذكاء الاصطناعي مجموعة كاملة من البطاقات ويفسرها معاً لوضعك — مجاناً، خلال ثوانٍ.',
    askCtaButton: 'اسأل قارئ التاروت بالذكاء الاصطناعي',
    askPath: '/ar/',
    languageSwitcherLabel: 'اقرأ هذه الصفحة باللغة:',
    languageNames: LANGUAGE_NAMES,
    prevCard: 'البطاقة السابقة',
    nextCard: 'البطاقة التالية',
    moreInArcana: (arcanaLabel) => `المزيد من بطاقات ${arcanaLabel}`,
    allCards: 'كل معاني بطاقات التاروت',
    cardOfTheDayTitle: 'بطاقة التاروت اليومية — قراءتك اليومية المجانية | NowTarot AI',
    cardOfTheDayDescription:
      'بطاقة التاروت لهذا اليوم مع معناها الكامل في الحب والعمل والمال. قراءة تاروت يومية مجانية تتجدد كل يوم.',
    cardOfTheDayHeading: 'بطاقة التاروت اليومية',
    cardOfTheDayIntro: (dateLabel) =>
      `اختر بطاقة لتاريخ ${dateLabel}. تبقى بطاقتك حتى تسحب من جديد.`,
    cardOfTheDayPickPrompt: 'اختر واحدة من البطاقات أدناه.',
    drawNewCardOfTheDay: 'اسحب بطاقة يوم جديدة',
    todaysCard: 'بطاقتك اليومية',
    readFullMeaning: (name) => `اقرأ المعنى الكامل لبطاقة ${name}`,
    localeTag: 'ar',
  },
};

STRINGS.zh = compileUi(zhUi, 'zh', 'zh-CN');
STRINGS.de = compileUi(deUi, 'de', 'de-DE');
STRINGS.pl = compileUi(plUi, 'pl', 'pl-PL');
STRINGS.pt = compileUi(ptUi, 'pt', 'pt-BR');
STRINGS.fr = compileUi(frUi, 'fr', 'fr-FR');

export default STRINGS;
